import { Link, Navigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { Alert } from '../components/ui/Alert.tsx';
import { PageHeader } from '../components/ui/Layout.tsx';
import { useAuth } from '../lib/auth.tsx';

/**
 * Route guards for the UI only: they decide what to show. The database (RLS, grants, triggers) and
 * the worker enforce the same rules for every request.
 */
export function Forbidden({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={title} />
      <Alert tone="warning">{children}</Alert>
      <p className="callout">
        <Link to="/" className="font-semibold text-brand-text underline">
          Back to Fundline
        </Link>
      </p>
    </div>
  );
}

export function AdminOnly({ children }: { children: ReactNode }) {
  const { claims } = useAuth();
  if (claims?.workspaceId && claims.role === 'admin') return <>{children}</>;
  return (
    <Forbidden title="Admins only">
      This page is for workspace admins. Ask an admin if you need a change here.
    </Forbidden>
  );
}

/** Pages that need an active workspace. The platform owner (no workspace) goes to the Platform page. */
export function MemberOnly({ children }: { children: ReactNode }) {
  const { claims } = useAuth();
  if (claims?.workspaceId && claims.role) return <>{children}</>;
  if (claims?.platformOwner) return <Navigate to="/platform" replace />;
  return <Forbidden title="No workspace">This page needs a workspace membership.</Forbidden>;
}

export function PlatformOwnerOnly({ children }: { children: ReactNode }) {
  const { claims } = useAuth();
  if (claims?.platformOwner) return <>{children}</>;
  return <Forbidden title="Not available">This page is for Fundline staff.</Forbidden>;
}

export function NotFoundPage() {
  return (
    <main className="mx-auto max-w-xl px-4 py-10">
      <Forbidden title="Page not found">There is nothing at this address.</Forbidden>
    </main>
  );
}
